import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import type { AdsMode } from "./AdsModeSelector";

const baseFaqs = [
  {
    question: "Est-ce que mes clients vont se rendre compte qu'ils parlent à une IA ?",
    answer: [
      "L'assistant se présente au nom de votre agence, avec un ton naturel et professionnel adapté à l'immobilier. Il ne récite pas un script — il conduit une vraie conversation : il pose des questions pertinentes, reformule, s'adapte aux réponses.",
      "La plupart des appelants ne font pas la différence. Et si quelqu'un préfère parler à un humain, l'assistant propose un rappel par un conseiller sans forcer l'échange.",
    ],
  },
  {
    question: "Combien de temps faut-il pour que ce soit opérationnel ?",
    answer: [
      "Moins de 30 minutes entre l'inscription et les premiers appels traités.",
      "Le processus : vous créez votre compte, configurez l'assistant (intentions, horaires, voix, FAQ), testez en conditions réelles, puis activez le renvoi d'appel depuis votre ligne existante.",
      "Vous pouvez tout faire en autonomie ou planifier un accompagnement avec notre équipe. Aucune compétence technique n'est requise.",
    ],
  },
  {
    question: "Est-ce que je dois changer de numéro de téléphone ?",
    answer: [
      "Non. Vous gardez votre numéro habituel, celui que vos clients connaissent déjà.",
      "Immovox vous attribue un numéro local français (non surtaxé) qui reste invisible pour vos clients. Vous configurez un simple renvoi d'appel : quand vous ne décrochez pas après X sonneries, l'appel est redirigé vers Immovox. Quand vous êtes disponible, vous décrochez normalement.",
    ],
  },
  {
    question: "Qu'est-ce que je reçois concrètement après chaque appel ?",
    answer: [
      "Un ticket structuré avec : l'intention identifiée (achat, vente, location, estimation), les informations collectées pendant l'échange, une synthèse IA en quelques lignes, un niveau de priorité basé sur vos critères, et les coordonnées de l'appelant.",
      "Vous recevez un e-mail instantané pour chaque appel qualifié. L'ensemble est consultable dans votre dashboard, où votre équipe peut s'attribuer les tickets et suivre les actions.",
    ],
  },
  {
    question: "Est-ce que ça remplace mon équipe ?",
    answer: [
      "Non. Immovox intervient quand personne n'est disponible. C'est un relais automatique, pas un remplacement.",
      "Quand vous êtes là, c'est vous qui décrochez. Quand vous ne l'êtes pas, votre prospect a quand même une réponse — et vous, une fiche pour le rappeler.",
    ],
  },
  {
    question: "Et si ça ne me convient pas ?",
    answer: [
      "Aucun engagement. Vous pouvez résilier, mettre en pause ou ajuster votre abonnement à tout moment depuis votre espace client.",
      "On ne vous enferme pas : si Immovox ne vous apporte pas de valeur, vous arrêtez. C'est aussi simple que ça.",
    ],
  },
];

const locationFaq = {
  question: "Ça fonctionne pour la gestion locative ?",
  answer: [
    "Oui. Immovox identifie l'intention de chaque appelant : recherche de location, demande de visite, question technique…",
    "Quand un bien génère un pic d'appels, l'assistant qualifie chaque dossier et vous recevez uniquement les candidats pertinents. Moins d'interruptions, des biens loués plus vite.",
  ],
};

const AdsFAQ = ({ mode }: { mode: AdsMode }) => {
  const containerRef = useScrollReveal({ staggerDelay: 80 });
  const faqs = mode === "location" ? [...baseFaqs, locationFaq] : baseFaqs;

  return (
    <section className="section-padding bg-secondary">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto" ref={containerRef}>
          <div className="text-center mb-12 scroll-reveal-item" data-scroll-item>
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Questions fréquentes
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground">
              Vos questions, nos réponses.
            </h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
              Les vraies questions que se posent les directeurs d'agence avant d'activer Immovox.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={faq.question} className="scroll-reveal-item" data-scroll-item>
                <AccordionItem
                  value={`faq-${i}`}
                  className="card-stripe border-border/50 px-6 rounded-xl"
                >
                  <AccordionTrigger className="text-left text-sm md:text-base font-semibold text-foreground hover:no-underline py-5 gap-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 space-y-3">
                    {faq.answer.map((paragraph, j) => (
                      <p key={j} className="text-sm text-muted-foreground leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              </div>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default AdsFAQ;
